import { formatUSTel } from '@/components/DynamicForm/utils/utils';
import { verifyZipCode } from '@/services/verifyZipCode';
import type { CFValidator, CFValidatorReturn } from '@/types/convertflow';
import { unacceptableInputs } from '@/utils/offensive-words';
import {
	emailSchema,
	firstNameSchema,
	lastNameSchema,
	phoneSchema,
	userDetailsErrorMessages,
	zipCodeSchema,
} from '@/utils/userDetailsValidation/userDetailsValidation';

export interface BuildUserDetailsCFValidatorsOptions {
	isSpanish?: boolean;
	/**
	 * Submit-time backend validation. CF calls every field validator with a
	 * submit context (`element_type === 'form'`, `event_type === 'submit'`, and
	 * the DOM `form`) right before it advances the funnel — this runs BEFORE the
	 * `cfSubmit` event fires, so it's the only place a backend `failure_list`
	 * can actually block navigation.
	 *
	 * The field validators of a single submit pass coalesce onto ONE call,
	 * passing the contact fields read off the form. It resolves to a map of CF
	 * field key (`name`, `email`, ...) -> error message; any entry blocks CF
	 * advance with that message. An empty object lets the submit proceed.
	 */
	validateSubmission?: (cfFields: Record<string, string>) => Promise<Record<string, string>>;
}

type ClientValidator = (value: unknown) => CFValidatorReturn | Promise<CFValidatorReturn>;

const isValidResult = (result: CFValidatorReturn): boolean =>
	result === true || result === null || result === undefined;

/** Contact field keys CF labels on the default form, read from the DOM at submit. */
const CF_CONTACT_KEYS = ['name', 'last_name', 'email', 'phone', 'zip_code'] as const;

/**
 * Returns the DOM form when CF is invoking the validator as part of a submit
 * (vs. a per-keystroke field check). Only then do we run the backend POST.
 */
const getSubmitForm = (options: Record<string, unknown> | undefined): HTMLFormElement | null => {
	if (!options) return null;
	if (options.element_type !== 'form' || options.event_type !== 'submit') return null;
	const form = options.form;
	if (form && typeof (form as { querySelector?: unknown }).querySelector === 'function') {
		return form as HTMLFormElement;
	}
	return null;
};

/**
 * Reads the contact field values (plus hidden extra fields and privacy_consent)
 * off the CF submit form. Exported so a native `submit` DOM listener can build
 * the same field shape CF's own `cfSubmit` event carries, as a fallback trigger
 * for CTAs whose button never dispatches that event.
 */
export const readSubmitFields = (form: HTMLFormElement): Record<string, string> => {
	const fields: Record<string, string> = {};
	for (const key of CF_CONTACT_KEYS) {
		const el = form.querySelector<HTMLInputElement>(`[name="contact[${key}]"]`);
		if (el) fields[key] = el.value ?? '';
	}
	// CF stores custom hidden fields (form_id, default_campaign_id, ...) under the
	// `contact[extra][<key>]` namespace. Read them all so marketing/attribution
	// fields survive this validation submit path, matching the cfSubmit event path.
	const extraEls = form.querySelectorAll<HTMLInputElement>('[name^="contact[extra]["]');
	extraEls.forEach((el) => {
		const key = /^contact\[extra\]\[([^\]]+)\]$/.exec(el.name)?.[1];
		if (key && el.value) fields[key] = el.value;
	});
	// use .checked to capture what the user actually selected.
	const consentEl = form.querySelector<HTMLInputElement>('[name="contact[privacy_consent]"]');
	if (consentEl) {
		const checkedValue = consentEl.checked ? 'true' : 'false';
		fields.privacy_consent = consentEl.type === 'checkbox' ? checkedValue : consentEl.value;
	}
	return fields;
};

const toStringValue = (value: unknown): string => {
	if (typeof value === 'string') return value;
	if (typeof value === 'number') return String(value);
	return '';
};

const runZod = (
	schema: { safeParse: (v: unknown) => { success: boolean; error?: { issues: Array<{ message: string }> } } },
	value: unknown
): true | string => {
	const result = schema.safeParse(value);
	if (result.success) return true;
	return result.error?.issues[0]?.message ?? 'Invalid value';
};

// CF invokes registered validators on every keystroke, so we memoize per-field
// by the normalized string input. Sync validators cache their resolved result;
// async validators cache the in-flight Promise so concurrent CF calls coalesce.
const memoSync = <R>(fn: (str: string) => R): ((str: string) => R) => {
	let lastKey: string | undefined;
	let lastResult: R;
	let primed = false;
	return (str: string) => {
		if (primed && str === lastKey) return lastResult;
		lastKey = str;
		lastResult = fn(str);
		primed = true;
		return lastResult;
	};
};

const memoAsync = <R>(fn: (str: string) => Promise<R>): ((str: string) => Promise<R>) => {
	let lastKey: string | undefined;
	let lastPromise: Promise<R> | undefined;
	return (str: string) => {
		if (lastPromise && str === lastKey) return lastPromise;
		lastKey = str;
		lastPromise = fn(str);
		return lastPromise;
	};
};

// CF renders its step-advance control as `button.cf-button` (it emits two —
// an in-container and an outside-container copy — so we toggle all matches).
const CF_SUBMIT_BUTTON_SELECTOR = 'button.cf-button';
const SUBMIT_PENDING_CLASS = 'cf-submit-pending';
const SUBMIT_SPINNER_CLASS = 'cf-submit-spinner';
const SUBMIT_SPINNER_STYLE_ID = 'cf-submit-spinner-style';

/** Injects the spinner keyframes/styles once per document. */
const ensureSpinnerStyles = (): void => {
	if (typeof document === 'undefined') return;
	if (document.getElementById(SUBMIT_SPINNER_STYLE_ID)) return;
	const style = document.createElement('style');
	style.id = SUBMIT_SPINNER_STYLE_ID;
	style.textContent =
		`@keyframes cf-submit-spin{to{transform:rotate(360deg)}}` +
		`.${SUBMIT_PENDING_CLASS}{cursor:progress;pointer-events:none;` +
		`display:inline-flex;align-items:center;justify-content:center}` +
		`.${SUBMIT_SPINNER_CLASS}{display:inline-block;width:1em;height:1em;flex:none;` +
		`border:2px solid currentColor;border-top-color:transparent;border-radius:50%;` +
		`animation:cf-submit-spin .6s linear infinite}`;
	document.head.appendChild(style);
};

// Stashes each button's original innerHTML while it's showing the spinner so we
// can restore the exact label markup once the POST settles.
const originalButtonHtml = new WeakMap<HTMLButtonElement, string>();

/**
 * Toggles a disabled "pending" state on CF's submit button(s) while the submit
 * validation pass is in flight: the button label is swapped for a spinner and
 * restored on settle. CF awaits our async validators, so without this the button
 * stays interactive (and clickable) during that window. Scoped to the submit
 * pass only — not per-keystroke checks. Driven by `createSubmitPendingTracker`.
 */
const setSubmitPending = (form: HTMLFormElement, pending: boolean): void => {
	if (typeof document === 'undefined') return;
	if (typeof form.querySelectorAll !== 'function') return;
	const buttons = form.querySelectorAll<HTMLButtonElement>(CF_SUBMIT_BUTTON_SELECTOR);
	buttons.forEach((button) => {
		if (pending) {
			if (originalButtonHtml.has(button)) return;
			ensureSpinnerStyles();
			originalButtonHtml.set(button, button.innerHTML);
			button.disabled = true;
			button.setAttribute('aria-busy', 'true');
			button.classList.add(SUBMIT_PENDING_CLASS);
			const spinner = document.createElement('span');
			spinner.className = SUBMIT_SPINNER_CLASS;
			spinner.setAttribute('aria-hidden', 'true');
			button.textContent = '';
			button.appendChild(spinner);
		} else {
			const original = originalButtonHtml.get(button);
			if (original === undefined) return;
			button.innerHTML = original;
			originalButtonHtml.delete(button);
			button.disabled = false;
			button.removeAttribute('aria-busy');
			button.classList.remove(SUBMIT_PENDING_CLASS);
		}
	});
};

/**
 * Ref-counts the gated validators of a submit pass that are still resolving so
 * the pending spinner stays up for the WHOLE pass, not just the backend POST.
 * The slowest field is `zip_code`: it awaits `verifyZipCode` and THEN the
 * coalesced backend POST. Tying the spinner to the POST alone lifted it while
 * `verifyZipCode` was still in flight, re-exposing the click window the spinner
 * exists to cover. CF invokes every field validator synchronously in one pass,
 * so the count peaks before any validator settles and only returns to zero once
 * every async validator (including zip) has resolved.
 */
const createSubmitPendingTracker = (): {
	begin: (form: HTMLFormElement) => void;
	end: () => void;
} => {
	let active = 0;
	let activeForm: HTMLFormElement | null = null;
	return {
		begin: (form) => {
			if (active === 0) {
				activeForm = form;
				setSubmitPending(form, true);
			}
			active += 1;
		},
		end: () => {
			if (active === 0) return;
			active -= 1;
			if (active === 0 && activeForm) {
				setSubmitPending(activeForm, false);
				activeForm = null;
			}
		},
	};
};

// CF blocks step advance when a validator resolves to an error string and
// focuses the field (adding `cf-error`), but it never renders that string —
// CF only paints messages it set itself via the native constraint API during
// its own synchronous submit pass, which has already finished by the time our
// async backend validator resolves. So we inject the message into CF's field
// container ourselves, mirroring the DOM approach `setSubmitPending` uses.
const FIELD_ERROR_CLASS = 'cf-backend-error-message';
const FIELD_ERROR_STYLE_ID = 'cf-backend-error-style';

/** Injects the backend-error message styles once per document. */
const ensureFieldErrorStyles = (): void => {
	if (typeof document === 'undefined') return;
	if (document.getElementById(FIELD_ERROR_STYLE_ID)) return;
	const style = document.createElement('style');
	style.id = FIELD_ERROR_STYLE_ID;
	style.textContent = `.${FIELD_ERROR_CLASS}{color:#c0202a;font-size:0.85em;line-height:1.3;margin-top:4px}`;
	document.head.appendChild(style);
};

/** CF wraps each contact field in `.cf-form-field`; resolve it via the input. */
const findFieldContainer = (form: HTMLFormElement, cfKey: string): HTMLElement | null => {
	const input = form.querySelector<HTMLElement>(`[name="contact[${cfKey}]"]`);
	if (!input || typeof input.closest !== 'function') return null;
	return input.closest('.cf-form-field');
};

/** Shows `message` under the field whose CF key is `cfKey`. */
const setFieldError = (form: HTMLFormElement, cfKey: string, message: string): void => {
	if (typeof document === 'undefined') return;
	if (typeof form.querySelector !== 'function') return;
	const container = findFieldContainer(form, cfKey);
	if (!container) return;
	ensureFieldErrorStyles();
	let node = container.querySelector<HTMLElement>(`.${FIELD_ERROR_CLASS}`);
	if (!node) {
		node = document.createElement('div');
		node.className = FIELD_ERROR_CLASS;
		node.setAttribute('role', 'alert');
		container.appendChild(node);
	}
	node.textContent = message;
};

/** Removes any injected backend-error messages so a fresh pass starts clean. */
const clearFieldErrors = (form: HTMLFormElement): void => {
	if (typeof form.querySelectorAll !== 'function') return;
	form.querySelectorAll(`.${FIELD_ERROR_CLASS}`).forEach((node) => node.remove());
};

/**
 * Runs every client validator for the fields actually present on the submit
 * form and returns false as soon as one fails. Used to suppress the backend
 * POST when a sibling field is already known to be invalid client-side — CF
 * blocks advance on that field anyway, so POSTing would only create a lead for
 * a form the user can't successfully submit. Values are read off the form and
 * the client validators are value-memoized, so this reuses results CF already
 * computed this pass rather than re-scanning/re-fetching.
 */
const allClientFieldsValid = async (
	clientValidators: Record<string, ClientValidator>,
	fields: Record<string, string>
): Promise<boolean> => {
	for (const key of Object.keys(fields)) {
		const validator = clientValidators[key];
		if (!validator) continue;
		const result = await validator(fields[key]);
		if (!isValidResult(result)) return false;
	}
	return true;
};

/**
 * Once a client field check has passed, lets the backend submit validation
 * block CF's step advance with a field-specific message. A non-valid client
 * result short-circuits (the field already failed locally).
 */
const applySubmitGate = (
	cfKey: string,
	resolved: CFValidatorReturn,
	form: HTMLFormElement,
	runSubmitValidation: (form: HTMLFormElement) => Promise<Record<string, string>>
): CFValidatorReturn | Promise<CFValidatorReturn> => {
	if (!isValidResult(resolved)) return resolved;
	return runSubmitValidation(form).then((errors) => {
		const message = errors[cfKey];
		if (message) {
			// CF won't render the string it blocks on, so surface it ourselves.
			setFieldError(form, cfKey, message);
			return message;
		}
		return resolved;
	});
};

/**
 * Builds the submit-time backend POST runner for one validator set, coalescing
 * the call across the field validators of a single CF submit pass: identical
 * form values -> one call. It re-fires only when the user edits a field (the
 * value signature changes), which is exactly when a previously rejected field
 * should be re-checked. Backend errors fail open so a transient failure never
 * traps the user on the step.
 */
const createRunSubmitValidation = (
	validateSubmission: (cfFields: Record<string, string>) => Promise<Record<string, string>>,
	clientValidators: Record<string, ClientValidator>
): ((form: HTMLFormElement) => Promise<Record<string, string>>) => {
	let lastSig: string | undefined;
	let lastErrors: Promise<Record<string, string>> | undefined;
	return (form) => {
		const fields = readSubmitFields(form);
		const sig = JSON.stringify(fields);
		if (lastErrors && sig === lastSig) return lastErrors;
		lastSig = sig;
		// Drop any messages from a prior pass so each field re-injects fresh.
		clearFieldErrors(form);
		lastErrors = (async () => {
			// Don't POST if any present field still fails its client check — CF
			// already blocks advance on that field, and POSTing would create a
			// lead the user can't actually submit (and a duplicate on retry).
			if (!(await allClientFieldsValid(clientValidators, fields))) return {};
			return validateSubmission(fields);
		})().catch(() => ({} as Record<string, string>));
		return lastErrors;
	};
};

/**
 * Builds the `withSubmitGate` wrapper for one validator set. Each returned
 * validator runs its client checks first and, once they pass, lets the backend
 * submit validation block CF's step advance — but only during an actual submit
 * pass. When there's no submit validation, the client validator is passed
 * through unchanged.
 *
 * The pending spinner is driven by a tracker shared across the set's validators
 * so it covers the whole pass: it goes up the moment any gated validator starts
 * async work and only clears once the last one (typically `zip_code`, whose
 * `verifyZipCode` runs before the coalesced POST) settles — not when the backend
 * POST alone resolves.
 */
const createSubmitGate = (
	runSubmitValidation: ((form: HTMLFormElement) => Promise<Record<string, string>>) | undefined
): ((cfKey: string, validator: ClientValidator) => CFValidator) => {
	if (!runSubmitValidation) {
		return (_cfKey, validator) => (value) => validator(value);
	}
	const pending = createSubmitPendingTracker();
	return (cfKey, validator) => (value, options) => {
		const clientResult = validator(value);
		const form = getSubmitForm(options);
		if (!form) return clientResult;
		const gated =
			clientResult instanceof Promise
				? clientResult.then((resolved) => applySubmitGate(cfKey, resolved, form, runSubmitValidation))
				: applySubmitGate(cfKey, clientResult, form, runSubmitValidation);
		// Only async outcomes hold the pass open; a sync client failure already
		// blocks CF on its own and needs no spinner window.
		if (gated instanceof Promise) {
			pending.begin(form);
			return gated.finally(() => pending.end());
		}
		return gated;
	};
};

/**
 * Builds the standard set of ConvertFlow field validators for the
 * UserDetails form step (name, last_name, email, phone, zip_code).
 *
 * Returned validators are registered via `useConvertFlowValidation` so that
 * CF gates step advance until the field is corrected.
 *
 * Behavior per field:
 * - `name` / `last_name` — Zod regex/length + profanity (`unacceptableInputs`)
 * - `email`              — Zod email
 * - `phone`              — Zod `(###) ###-####` regex
 * - `zip_code`           — Zod 5- or 9-digit regex, then async `verifyZipCode`
 *
 * Errors thrown inside validators are caught by CF and treated as VALID
 * (fail-open). Network errors in `verifyZipCode` are already swallowed by
 * the service (returns `true`) so async validation is fail-open by design.
 */
export const buildUserDetailsCFValidators = (
	opts: BuildUserDetailsCFValidatorsOptions = {}
): Record<string, CFValidator> => {
	const { isSpanish = false, validateSubmission } = opts;
	const messages = userDetailsErrorMessages(isSpanish);

	const fnSchema = firstNameSchema(isSpanish);
	const lnSchema = lastNameSchema(isSpanish);
	const emSchema = emailSchema(isSpanish);
	const phSchema = phoneSchema(isSpanish);
	const zpSchema = zipCodeSchema(isSpanish);

	const buildNameValidator = (schema: ReturnType<typeof firstNameSchema>, fieldKey: string): ClientValidator => {
		const inner = memoSync<true | string>((str) => {
			const schemaResult = runZod(schema, str);
			if (schemaResult !== true) return schemaResult;
			const profane = unacceptableInputs([{ name: fieldKey, value: str }]);
			if (profane.length > 0) return messages.profanity;
			return true;
		});
		return (value) => inner(toStringValue(value));
	};

	const emailInner = memoSync<true | string>((str) => runZod(emSchema, str));
	// CF's plain phone input doesn't go through our TelInput formatter, so
	// normalize the digits to `(xxx) xxx-xxxx` before applying the Zod
	// regex that the rest of the codebase already expects.
	const phoneInner = memoSync<true | string>((str) => runZod(phSchema, formatUSTel(str)));
	const zipInner = memoAsync<true | string>(async (str) => {
		const schemaResult = runZod(zpSchema, str);
		if (schemaResult !== true) return schemaResult;
		const isValid = await verifyZipCode(str);
		if (!isValid) return messages.zipCode;
		return true;
	});

	const nameClient = buildNameValidator(fnSchema, 'name');
	const lastNameClient = buildNameValidator(lnSchema, 'last_name');
	const emailClient: ClientValidator = (value) => emailInner(toStringValue(value));
	const phoneClient: ClientValidator = (value) => phoneInner(toStringValue(value));
	const zipClient: ClientValidator = (value) => zipInner(toStringValue(value));

	const clientValidators: Record<string, ClientValidator> = {
		name: nameClient,
		last_name: lastNameClient,
		email: emailClient,
		phone: phoneClient,
		zip_code: zipClient,
	};

	const runSubmitValidation = validateSubmission
		? createRunSubmitValidation(validateSubmission, clientValidators)
		: undefined;
	const withSubmitGate = createSubmitGate(runSubmitValidation);

	return {
		name: withSubmitGate('name', nameClient),
		last_name: withSubmitGate('last_name', lastNameClient),
		email: withSubmitGate('email', emailClient),
		phone: withSubmitGate('phone', phoneClient),
		zip_code: withSubmitGate('zip_code', zipClient),
	};
};
